import {debounce} from 'lodash-es';
import {
    AgGrid,
    AgGridColumnMenuTrigger,
    AgGridExportMenu,
    AgGridProvider,
    AgGridTimestamp,
    AgGridToggleShowFilters,
    NyDateTimeInput,
    NySelectInput
} from 'nyse-web-tools-common/lib/react';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import React from 'react';

import {NavigationSectionMenu} from '../components/navigation/navigation.section.menu';
import {FirmSelect} from '../components/selects/firm.select';
import {setColumns} from '../stores/columnDefs/columnDefs.actions';
import {getMyVolumesBrokerRank} from '../stores/myVolumes/myVolumesBrokerRank.actions';
import {getMyVolumesFilters} from '../stores/myVolumes/myVolumesFilters.actions';

@connect(
    [
        'firms',
        'columnDefs',
        'myVolumesBrokerRank',
        'myVolumesFilters',
        'platform',
        'route'
    ],
    {
        getMyVolumesBrokerRank,
        getMyVolumesFilters,
        setColumns
    }
)
export class MyVolumesBrokerRankPage extends React.Component {
    state = {
        filterValues: {},
        views: [
            {
                title: 'CADV',
                url: '/:platform/volumes/cadv'
            },
            {
                title: 'Broker Rank',
                url: '/:platform/volumes/broker-rank'
            },
            {
                title: 'Trade Count',
                url: '/:platform/volumes/trade-count'
            },
            {
                title: 'Search',
                url: '/:platform/volumes/search'
            }
        ]
    };

    _initialFilters = {
        crd: this.props.route.params.crd || (this.props.firms[0] || {}).crd,
        date: '',
        participantId: '',
        mpSubId: ''
    };

    _getMyVolumesFilters = debounce(this.props.getMyVolumesFilters, 500);

    componentDidUpdate(prevProps, prevState) {
        const hasCrdChanged =
            prevState.filterValues.crd !== this.state.filterValues.crd;
        const hasDateChanged =
            (prevState.filterValues.date || '').toString() !==
            (this.state.filterValues.date || '').toString();
        if (hasCrdChanged || hasDateChanged) {
            this._getMyVolumesFilters(this.state.filterValues);
        }
    }

    componentWillUnmount() {
        this._getMyVolumesFilters.cancel();
    }

    _onDateChange = ({setValue}) => {
        setValue('mpSubId', '');
    };

    _onFirmChange = ({setValue}) => {
        setValue('mpSubId', '');
    };

    _onFiltersChanged = (filterValues) => {
        this.setState({filterValues});
    };

    render() {
        const {mpSubIds} = this.props.myVolumesFilters;
        return (
            <>
                <div className='row'>
                    <div className='col-md-12 col-lg-12 col-xl-12 col-sm-12 col-xs-12'>
                        <NavigationSectionMenu
                            queryParams={this.state.filterValues}
                            views={this.state.views}
                        />
                    </div>
                </div>
                <AgGridProvider
                    columnDefs={this.props.columnDefs.myVolumesBrokerRank}
                    columnNamespace='myVolumesBrokerRank'
                    filtersClassName='row space-bottom-1'
                    initialFilters={this._initialFilters}
                    onColumnsChanged={this.props.setColumns}
                    onFiltersChanged={this._onFiltersChanged}
                    onLoad={this.props.getMyVolumesBrokerRank}
                    submitOnLoad={true}
                    submitOnNamespaceChanged={true}
                    {...this.props.myVolumesBrokerRank}>
                    <AgGridProvider.Filters>
                        <div className='form-group col-xl-2 col-lg-2 col-md-2 col-sm-3 col-xs-12'>
                            <label>Date</label>
                            <NyDateTimeInput
                                className='form-control'
                                format='MM/yyyy'
                                initialValue={this.props.route.params.date}
                                minView='months'
                                name='date'
                                onChange={this._onDateChange}
                            />
                        </div>
                        {this.props.firms.length > 1 && (
                            <div className='form-group col-xl-2 col-lg-2 col-md-2 col-md-2 col-sm-3 col-xs-12'>
                                <label>Firm</label>
                                <FirmSelect
                                    name='crd'
                                    required={true}
                                    showInactive={true}
                                    onChange={this._onFirmChange}
                                />
                            </div>
                        )}
                        <div className='form-group col-lg-2 col-md-2 col-md-2 col-sm-3 col-xs-12'>
                            <label>Mnemonic</label>
                            <NySelectInput
                                initialValue={this.props.route.params.mpSubId}
                                name='mpSubId'
                                disabled={mpSubIds.length === 0}>
                                <option value=''>-</option>
                                {mpSubIds.map((mpSubId) => (
                                    <option key={mpSubId} value={mpSubId}>
                                        {mpSubId}
                                    </option>
                                ))}
                            </NySelectInput>
                        </div>
                    </AgGridProvider.Filters>

                    <div className='row space-bottom-1'>
                        <div className='col-md-12 col-lg-12 col-xl-12 col-sm-12 col-xs-12'>
                            <div className='flex-row flex-align-bottom'>
                                <AgGridProvider.SubmitBtn>
                                    Search
                                </AgGridProvider.SubmitBtn>
                                <AgGridProvider.ResetBtn>
                                    Reset
                                </AgGridProvider.ResetBtn>
                                <div className='btn-circle-grp flex-pull-right'>
                                    <AgGridToggleShowFilters />
                                    <AgGridExportMenu
                                        customHeader={
                                            this.props.myVolumesBrokerRank
                                                .timestamp
                                        }
                                        exportFileName='myVolumesBrokerRank'
                                        exportSheetName={
                                            this.props.platform.name
                                        }
                                    />
                                    <AgGridColumnMenuTrigger />
                                </div>
                            </div>
                        </div>
                    </div>

                    <AgGrid reactNext={true} treeData={true} />

                    <div className='row space-top-1'>
                        <div className='col-md-12 col-lg-12 col-xl-12 col-sm-12 col-xs-12'>
                            <AgGridTimestamp timestampPrefix='Volumes' />
                        </div>
                    </div>

                    <div className='disclaimer space-top-1'>
                        This information is provided 'as is', and NYSE, NYSE
                        Arca, NYSE American, NYSE Arca Options, NYSE American
                        Options and their respective affiliates do not warrant
                        the accuracy, adequacy, timeliness or completeness of
                        this information. The information, which excludes any
                        data from the current trading day, is intended to be
                        used for informational purposes only and will not be
                        used to determine qualification for any rebates or
                        incentives.
                    </div>
                </AgGridProvider>
            </>
        );
    }
}
